import type { Id } from "@repo/convex/dataModel";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

interface ProjectDragState {
  isDragging: boolean;
  projectId: Id<"projects"> | null;
  sourceFolderId: Id<"projectFolders"> | undefined | null;
  // undefined = project is in root/"My Projects"
  // null = no drag active
}

interface ProjectDragContextValue extends ProjectDragState {
  startDrag: (projectId: Id<"projects">, sourceFolderId: Id<"projectFolders"> | undefined) => void;
  endDrag: () => void;
}

const ProjectDragContext = createContext<ProjectDragContextValue | null>(null);

export function ProjectDragProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ProjectDragState>({
    isDragging: false,
    projectId: null,
    sourceFolderId: null,
  });

  const startDrag = useCallback(
    (projectId: Id<"projects">, sourceFolderId: Id<"projectFolders"> | undefined) => {
      setState({ isDragging: true, projectId, sourceFolderId });
    },
    []
  );

  const endDrag = useCallback(() => {
    setState({ isDragging: false, projectId: null, sourceFolderId: null });
  }, []);

  const value = useMemo(() => ({ ...state, startDrag, endDrag }), [state, startDrag, endDrag]);

  return <ProjectDragContext.Provider value={value}>{children}</ProjectDragContext.Provider>;
}

export function useProjectDrag() {
  const context = useContext(ProjectDragContext);
  if (!context) {
    throw new Error("useProjectDrag must be used within a ProjectDragProvider");
  }
  return context;
}
